import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  TextField,
  Typography,
  Button
} from "@mui/material";

import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  getTrainingCourse,
  getTrainingRoles,
  getTrainingUsers,
  getTableSettings,
  saveTableSettings,
  deleteTableSettings
} from "../../../store/thunks/portalManagementThunk";
import { MRT_ColumnDef, useMaterialReactTable, MaterialReactTable, MRT_Row } from "material-react-table";
import TableTemplate from "../../reusableComponents/tableTemplate/tableTemplate";
import { useShellNav } from "../../commonComponents/appShell/shellNav";
import UserManagement from "./userManagement/userManagement";
import { UserType } from "../../../models/userType";
import { Program } from "../../../models/program";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  CourseItem,
  DATE_PRESETS,
  DatePresetKey,
  completedDateOf,
  finalScoreOf,
  hasFinalScore,
  stripEmptyRanges,
  presetRange,
  withinDateRange,
} from "./trainingViewModel";
import {
  filterAutofillGuard,
  dash,
  formatDate,
  formatDateOnly,
  stableStringify,
  getDefaultTableColumnOrder,
  getDefaultTableColumnSizing,
  resetTableColumnOrder,
  resetTableColumnSizing,
  resetTableSettings
} from "../../../utils/tableUtils";
import { tokens } from "../../../theme";
import SectionCard from "../../reusableComponents/sectionCard/sectionCard";

const userQueryClient = new QueryClient();

const defaultSelectedCourse = "All Courses";

// finalScoreOf / hasFinalScore / completedDateOf live in trainingViewModel.ts
// so the unit tests exercise the exact code the view runs.

/**
 * Grade items in the order they were completed. The detail table has sorting
 * disabled, so the data arrives sorted rather than being sorted in the table.
 * Items with no finish time sit at the end — they have not happened yet.
 */
const byCompletion = (items: any[] = []) =>
  items.slice().sort((a, b) => {
    const x = a?.time_finish ? new Date(a.time_finish).getTime() : Infinity;
    const y = b?.time_finish ? new Date(b.time_finish).getTime() : Infinity;
    return x - y;
  });

// Date-preset model — DATE_PRESETS, presetRange, withinDateRange — lives in
// trainingViewModel.ts so the unit tests exercise the exact code the view runs
// without importing the component tree.

/** Date columns a preset can be applied to, in the order they are offered. */
const DATE_COLUMNS: { id: string; label: string }[] = [
  { id: "assigned_date", label: "Assigned" },
  { id: "last_access", label: "Last Access" },
  { id: "completed_date", label: "Completed" },
];

/** Column values are Dates so sorting, filtering and formatting all agree. */
const asDate = (value: any): Date | undefined => (value ? new Date(value) : undefined);

const SETTINGS_TARGET = { program_index: Program.Index.Training, target_table: "gradeItemTable" };
const SETTINGS_KEY = `${SETTINGS_TARGET.program_index}/${SETTINGS_TARGET.target_table}`;

const TrainingView = () => {
  // ── Store ──
  const dispatch = useAppDispatch();

  const { user, loggedIn } = useAppSelector((state: any) => state.authentication);

  const {
    selectedClient,
    selectedUserType,
    selectedSchoolDealer,
    studentGradeItemsRawData,
    trainingUsers,
    trainingUsersLoading,
    trainingCourseLoading,
    tableSettings
  } = useAppSelector((state: any) => state.portalManagement);

  // undefined = not fetched; null = none saved; object = saved
  const savedSettings = tableSettings?.[SETTINGS_KEY];

  // ── Local state ──
  const [rows, setRows] = useState<CourseItem[]>([]);
  const [courseOptions, setCourseOptions] = useState<string[]>([]);
  const [selectedCourseGI, setSelectedCourseGI] = useState<string | null>(defaultSelectedCourse);
  const { selectedItemId: selectedNavigation } = useShellNav();

  // Persisted view state: order, sorting, visibility, sizing, density;
  // filters/search/pagination excluded; "Save View" appears when dirty
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [sorting, setSorting] = useState<any[]>([]);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
  const [density, setDensity] = useState<any>("comfortable");
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  // columnId -> preset key. "" means the filter was set by hand.
  const [dateRanges, setDateRanges] = useState<Record<string, DatePresetKey | "">>({});
  const settingsFetchedRef = useRef(false);

  // ── Derived data ──
  const isAdmin = useMemo(() => {
    const match: any = (trainingUsers as any[] | undefined)?.find(
      (u: any) => u.clients_user_id === user?.client_user_index
    );
    return match?.role_type === UserType.Name.LWS_ADMIN;
  }, [trainingUsers, user]);

  const filteredData = useMemo(() => {
    const data = (selectedCourseGI && selectedCourseGI !== defaultSelectedCourse)
      ? rows.filter((row) => row.course === selectedCourseGI)
      : rows;

    return data.map((row) => ({
      ...row,
      gradeItems: (row.course.toLowerCase().includes('wdc') || !row.course.toLowerCase().includes('csa'))
        ? (row.gradeItems ?? []).filter((item) =>
            (item.name || '').toLowerCase().includes('pre-test') ||
            (item.name || '').toLowerCase().includes('final assessment')
          )
        : (row.gradeItems ?? []),
    }));
  }, [rows, selectedCourseGI]);

  const columns = useMemo<MRT_ColumnDef<CourseItem>[]>(() => [
    {
      accessorKey: 'course',
      header: 'Course',
      size: 200,
    },
    {
      accessorKey: 'student',
      header: 'Student',
      size: 150,
    },
    ...(isAdmin ? [{
      accessorKey: 'email',
      header: 'Email',
      size: 150,
    } as MRT_ColumnDef<CourseItem>] : []),
    {
      header: 'Final Score',
      id: 'finalScore',
      size: 150,
      accessorFn: (row) => finalScoreOf(row),
      Cell: ({ renderedCellValue }) => {
        if (renderedCellValue === '') return dash;
        if (renderedCellValue === 'Multiple') return <em>Multiple</em>;
        return renderedCellValue;
      }
    },
    {
      accessorKey: 'assigned_date',
      filterVariant: 'date-range',
      filterFn: withinDateRange,
      // A custom filterFn has no name in MRT's localization, so the mode picker
      // renders "Filter Mode: undefined". There is only one mode here anyway.
      enableColumnFilterModes: false,
      sortingFn: 'datetime',
      header: 'Assigned Date',
      size: 200,
      accessorFn: (row) => asDate(row.assigned_date),
      Cell: ({ cell }) => formatDateOnly(cell.getValue<Date>())
    },
    {
      accessorKey: 'completed_status',
      header: 'Status',
      size: 150,
      accessorFn: (row) =>
        // A score means it is finished even if completed_status still says Active.
        row.completed_status || hasFinalScore(row) ? 'Completed' : 'Active'
    },
    {
      accessorKey: 'last_access',
      filterVariant: 'date-range',
      filterFn: withinDateRange,
      // A custom filterFn has no name in MRT's localization, so the mode picker
      // renders "Filter Mode: undefined". There is only one mode here anyway.
      enableColumnFilterModes: false,
      sortingFn: 'datetime',
      header: 'Last Access',
      size: 200,
      accessorFn: (row) => {
        const completed = row.completed_status
        return asDate((!completed || row.completed_date) ? row.last_access : '');
      },
      Cell: ({ cell }) => formatDateOnly(cell.getValue<Date>())
    },
    {
      accessorKey: 'completed_date',
      filterVariant: 'date-range',
      filterFn: withinDateRange,
      // A custom filterFn has no name in MRT's localization, so the mode picker
      // renders "Filter Mode: undefined". There is only one mode here anyway.
      enableColumnFilterModes: false,
      sortingFn: 'datetime',
      header: 'Completed Date',
      size: 200,
      accessorFn: (row) =>
        // Scored but no completion date recorded: the item that finished it.
        asDate(completedDateOf(row)),
      Cell: ({ cell }) => formatDateOnly(cell.getValue<Date>())
    },
    {
      accessorKey: 'state',
      header: 'State',
      size: 100,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return (value === '') ? dash : value
      }
    },
    {
      accessorKey: 'country',
      header: 'Country',
      size: 100,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return (value === '') ? dash : value
      }
    },
    ...(isAdmin ? [{
      accessorKey: 'school',
      header: 'School',
      size: 100,
      Cell: ({ cell }: { cell: any }) => {
        const value = cell.getValue() as string;
        return (value === '') ? dash : value
      }
    } as MRT_ColumnDef<CourseItem>] : [])
  ], [isAdmin]);

  const defaultColumnOrder = useMemo(
    () => getDefaultTableColumnOrder(columns, ["mrt-row-expand"]),
    [columns]
  );

  const defaultColumnSizing = useMemo(
    () => getDefaultTableColumnSizing(columns),
    [columns]
  );

  // ── Functions ──
  const currentSettings = useMemo(() => ({
    columnOrder,
    sorting,
    columnVisibility,
    columnSizing,
    density,
    // A view preference, not a filter: the preset is saved, the dates it
    // resolves to are not. Cleared ("") entries are stripped so toggling a
    // preset on and off compares equal to never having touched it.
    dateRanges: stripEmptyRanges(dateRanges),
  }), [columnOrder, sorting, columnVisibility, columnSizing, density, dateRanges]);

  const settingsDirty = useMemo(() => {
    if (!columnOrder.length) return false; // not hydrated yet
    // Baseline gets the same normalization as live state; stale column ids
    // can't pin the button on permanently
    const baseline = {
      ...resetTableSettings(savedSettings ?? {}, defaultColumnOrder, defaultColumnSizing),
      dateRanges: stripEmptyRanges(savedSettings?.dateRanges),
    };
    return stableStringify(currentSettings) !== stableStringify(baseline);
  }, [currentSettings, savedSettings, defaultColumnOrder, defaultColumnSizing, columnOrder]);

  // Equal-to-defaults means there is nothing worth persisting: hide Save View
  // and let Reset View (which deletes the saved blob) be the way back.
  const matchesDefaults = useMemo(() => {
    const defaults = {
      ...resetTableSettings({}, defaultColumnOrder, defaultColumnSizing),
      dateRanges: {},
    };
    return stableStringify(currentSettings) === stableStringify(defaults);
  }, [currentSettings, defaultColumnOrder, defaultColumnSizing]);

  const applyDatePreset = (columnId: string, key: DatePresetKey | "") =>
    setDateRanges((prev) => ({ ...prev, [columnId]: key }));

  // dateRanges is the source of truth; the preset's filter is derived from it
  // every render. Keeping the two in state separately meant mount-time
  // reconciliation could clear a preset before its filter had been applied.
  const effectiveFilters = useMemo(() => {
    const fromPresets = DATE_COLUMNS.filter(({ id }) => dateRanges[id]).map(({ id }) => ({
      id,
      value: presetRange(dateRanges[id] as DatePresetKey),
    }));
    const manual = columnFilters.filter(
      (f: any) => !fromPresets.some((pf) => pf.id === f.id)
    );
    return [...manual, ...fromPresets];
  }, [columnFilters, dateRanges]);

  // Editing a date filter by hand drops that column back to Custom, so the
  // select never claims a preset the filter no longer matches.
  const handleColumnFiltersChange = (updater: any) => {
    setColumnFilters((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      setDateRanges((ranges) => {
        const cleared = { ...ranges };
        for (const { id } of DATE_COLUMNS) {
          if (!cleared[id]) continue;
          const value = next.find((f: any) => f.id === id)?.value;
          // No entry at all means the table is still settling, not that anyone
          // cleared it — only an actual differing value drops the preset.
          if (!Array.isArray(value) || !value[0]) continue;
          const [from] = presetRange(cleared[id] as DatePresetKey);
          if (dayjs(value[0]).valueOf() !== from.valueOf()) cleared[id] = "";
        }
        return cleared;
      });
      return next;
    });
  };

  const handleSaveView = () =>
    dispatch(saveTableSettings({ ...SETTINGS_TARGET, settings: currentSettings }));

  const handleResetView = () =>
    dispatch(deleteTableSettings({ ...SETTINGS_TARGET }));

  const gradeItemsDetailPanel = ({ row }: { row: MRT_Row<CourseItem> }) => {
    return (
      <Box sx={{ margin: 2, width: "100%" }}>
        <MaterialReactTable
          columns={[
            {
              accessorKey: 'name',
              header: 'Grade Item',
              size: 340,
            },
            {
              accessorKey: 'state',
              header: 'State',
              size: 100,
              Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return value.charAt(0).toUpperCase() + value.slice(1)
              }
            },
            {
              accessorKey: 'time_start',
              header: 'Started On',
              size: 190,
              Cell: ({ cell }) => formatDate(cell.getValue<Date>())
            },
            {
              accessorKey: 'time_finish',
              header: 'Completed On',
              size: 190,
              Cell: ({ cell }) => formatDate(cell.getValue<Date>())
            },
            {
              accessorKey: 'time_taken',
              header: 'Time Taken',
              size: 200,
              Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (value === '-') ? dash : value
              }
            },
            {
              accessorKey: 'correct',
              header: 'Correct',
              size: 100,
              Cell: ({ cell }) => {
                const value = cell.getValue<number>();
                return (value === -1) ? dash : value.toFixed(0)
              }
            },
            {
              accessorKey: 'incorrect',
              header: 'Incorrect',
              size: 100,
              Cell: ({ cell }) => {
                const value = cell.getValue<number>();
                return (value === -1) ? dash : value.toFixed(0)
              }
            },
            {
              accessorKey: 'grade',
              header: 'Grade',
              size: 100,
              Cell: ({ cell }) => {
                const value = cell.getValue<number>();
                return (value === -1) ? dash : (<strong> {value.toFixed(0)} </strong>)
              }
            }
          ]}
          data={byCompletion(row.original.gradeItems)}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={false}
          enableBottomToolbar={false}
          enableTopToolbar={false}
          muiTableBodyRowProps={{ hover: false }}
          muiTableProps={{
            size: 'small',
          }}
        />
      </Box>
    );
  };

  // ── Effects ──
  useEffect(() => {
    if (loggedIn && !trainingUsers?.length && !trainingUsersLoading) {
      dispatch(getTrainingUsers({}));
    }
  }, [dispatch, loggedIn, trainingUsers, trainingUsersLoading]);

  useEffect(() => {
    if (loggedIn && selectedClient) {
      dispatch(
        getTrainingCourse({
          customer_id: selectedClient.customer_id,
          account_type: selectedUserType?.name,
          people_user_id: selectedSchoolDealer?.id
        }));
        if(isAdmin) dispatch(getTrainingRoles({}))
    }
  }, [dispatch, isAdmin, loggedIn, selectedClient, selectedUserType, selectedSchoolDealer])

  // Fetch saved settings once per mount; the slice is redux-persisted, so a
  // cached value may exist; still refresh from the server
  useEffect(() => {
    if (loggedIn && !settingsFetchedRef.current) {
      settingsFetchedRef.current = true;
      dispatch(getTableSettings({ ...SETTINGS_TARGET }));
    }
  }, [dispatch, loggedIn]);

  // Latest defaults via ref; lets the hydrate effect depend on savedSettings
  // alone without re-firing on column-set changes
  const defaultsRef = useRef({ columnOrder: defaultColumnOrder, columnSizing: defaultColumnSizing });
  defaultsRef.current = { columnOrder: defaultColumnOrder, columnSizing: defaultColumnSizing };

  // Hydrate when the saved blob changes (fetched/saved/reset); deliberately
  // not on column-set changes — that would wipe unsaved tweaks; the next
  // effect handles those in place
  useEffect(() => {
    const r = resetTableSettings(savedSettings ?? {}, defaultsRef.current.columnOrder, defaultsRef.current.columnSizing);
    setColumnOrder(r.columnOrder);
    setSorting(r.sorting);
    setColumnVisibility(r.columnVisibility);
    setColumnSizing(r.columnSizing);
    setDensity(r.density);

    // Re-resolve each saved preset against today, then rebuild its filter.
    setDateRanges((savedSettings?.dateRanges ?? {}) as Record<string, DatePresetKey | "">);
  }, [savedSettings]);

  // Column set changed (admin columns arrive with trainingUsers); reset the
  // current state against it in place; unsaved tweaks survive
  useEffect(() => {
    setColumnOrder((prev) => (prev.length ? resetTableColumnOrder(prev, defaultColumnOrder) : prev));
    setColumnSizing((prev) => resetTableColumnSizing(prev, defaultColumnOrder, defaultColumnSizing));
  }, [defaultColumnOrder, defaultColumnSizing]);

  useEffect(() => {
    if(studentGradeItemsRawData && studentGradeItemsRawData?.length > 0) {
      const studentGradeItems = studentGradeItemsRawData as CourseItem[];
      setRows(studentGradeItems);
      const uniqueCourses = Array.from(new Set(studentGradeItems.map((r: CourseItem) => r.course))).sort();
      setCourseOptions([defaultSelectedCourse, ...uniqueCourses]);
    }
  }, [studentGradeItemsRawData]);

  // ── Table ──
  const gradeItemTable = useMaterialReactTable({
    muiFilterTextFieldProps: filterAutofillGuard,
    columns,
    data: filteredData,
    state: {
      isLoading: trainingCourseLoading,
      columnOrder,
      sorting,
      columnVisibility,
      columnSizing,
      density,
      columnFilters: effectiveFilters,
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnOrderChange: setColumnOrder,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onDensityChange: setDensity,
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    // Sticky needs the table itself to be the scroll container — pagination is
    // off, so without a maxHeight the page scrolls and the header leaves with it.
    // MRT gives a sticky thead opacity 0.97, so rows ghost through it while
    // pinned; force it opaque (the cells already carry the paper background).
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { maxHeight: "72vh" } },
    muiTableHeadProps: { sx: { opacity: 1 } },
    muiTableHeadCellProps: { sx: { backgroundColor: "background.paper" } },
    // Resizing defaults to 'grid-no-grow' (literal px widths); 'grid' keeps
    // columns filling the container
    layoutMode: "grid",
    enableExpanding: true,
    enableExpandAll: true,
    getRowId: (row) => `${row.course}-${row.student}`,
    renderDetailPanel: gradeItemsDetailPanel,
    // In 'grid' layout the detail panel no longer spans the row; force full width
    muiDetailPanelProps: {
      sx: { width: "100%" },
    },
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {settingsDirty && !matchesDefaults && (
          <Button variant="contained" size="small" onClick={handleSaveView}>
            Save View
          </Button>
        )}
        {savedSettings && (
          <Button size="small" onClick={handleResetView}>
            Reset View
          </Button>
        )}
      </Box>
    ),
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true
    },
    muiTablePaperProps: {
      elevation: 2,
      sx: { width: '100%' },
    },
    paginationDisplayMode: "pages",
    positionToolbarAlertBanner: "bottom",
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [5, 10, 20, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
  });

  return (
    <Grid container sx={{}}>

      {selectedNavigation === "users" && (
        <Grid
          // container
          item
          xs={12}
          sx={{
            borderRight: `10px solid ${tokens.color.surface}`,
            borderLeft: `10px solid ${tokens.color.surface}`,
            backgroundColor: tokens.tile.panelTint,
          }}
          >
          <QueryClientProvider client={userQueryClient}>
            <UserManagement />
          </QueryClientProvider>
        </Grid>
      )}

      {selectedNavigation === "user_grades" && (
        <Grid
          container
          item
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 20px 10px 20px",
          }}
          >
          <Grid container sx={{}}>
            <Grid container item xs={12} sx={{ padding: "10px" }}>
            <SectionCard title="Student Grades">
              <Grid
                container
                item
                xs={12}
                sx={{
                  padding: "10px 20px 10px 20px",
                  border: tokens.cardChrome.bodyBorder,
                  backgroundColor: tokens.cardChrome.bodyBg,
                  gap: 2,
                }}
              >
                <Grid container item xs={12}>
                  <Grid
                    item
                    xs={4}
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                    }}
                  >
                    <FormControl fullWidth>
                      <Autocomplete
                        id="course_selection"
                        value={selectedCourseGI ?? defaultSelectedCourse}
                        onChange={(_, newValue) => {
                          setSelectedCourseGI(newValue ?? defaultSelectedCourse);
                        }}
                        options={courseOptions}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Courses"
                            placeholder="Select Courses"
                          />
                        )}
                      disableClearable={selectedCourseGI === defaultSelectedCourse}
                      disabled={rows?.length === 1}
                      />
                    </FormControl>
                  </Grid>

                  {/* One range per date column. Saved with the view, as a
                      preset rather than the dates it currently resolves to. */}
                  <Grid
                    item
                    xs={8}
                    sx={{ display: "flex", flexDirection: "row", gap: 2, pl: 2 }}
                  >
                    {/* No size prop on these: they sit beside Courses and have
                        to match its height. */}
                    {DATE_COLUMNS.map(({ id, label }) => (
                      <FormControl key={id} fullWidth>
                        <Autocomplete
                          id={`range_${id}`}
                          value={dateRanges[id] ? DATE_PRESETS[dateRanges[id] as DatePresetKey] : null}
                          onChange={(_, picked) => {
                            const key = (Object.keys(DATE_PRESETS) as DatePresetKey[])
                              .find((k) => DATE_PRESETS[k] === picked);
                            applyDatePreset(id, key ?? "");
                          }}
                          options={Object.values(DATE_PRESETS)}
                          renderInput={(params) => (
                            <TextField {...params} label={`${label} range`} placeholder="All time" />
                          )}
                        />
                      </FormControl>
                    ))}
                  </Grid>
                </Grid>
                <Grid container sx={{}}>
                  <TableTemplate tableData={gradeItemTable} />
                </Grid>
              </Grid>
            </SectionCard>
            </Grid>
          </Grid>
      </Grid>)}
    </Grid>
  );
};
export default TrainingView;
